import React, {ReactElement, ReactNode} from "react";
import PortalDialogType from "../../../../dialog/PortalDialogType";
import MimeTypes from "../../../mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import MimeTypeFilter from "../../../mimetype/MimeTypeFilter";
import Parameter from "../../../../dialog/parameter/Parameter";
import PortalDocument from "../../../PortalDocument";
import PortalDocumentManager from "../../../PortalDocumentManager";
import DialogDefault from "../../../../dialog/parameter/PortalDialogDefault";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {InputNumber} from "primereact/inputnumber";
import AnnotationStampFunctionFields from "../../../../dialog/field/annotation/AnnotationStampFunctionFields";
import validator from "validator";
import MimeType from "../../../mimetype/MimeType";
import Message from "../../../../../shared/log/Message";
import {MessageType} from "../../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	Rectangle,
	RubberStampIcon,
	Security,
	Settings,
	SignatureEditProfile,
	UserCredentials
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../../../dialog/PortalDialogSecurity";
import Translate from "../../../../../shared/translation/Translate";
import Immutable from "../../../../../shared/immutable/Immutable";
import SidebarFunction, {SidebarFunctionProperties, SidebarFunctionState} from "../SidebarFunction";
import * as ReactDOMServer from "react-dom/server";
import {ApprovedStamp} from "../../../../../shared/annotation/stamp/ApprovedStamp";
import {ExperimentalStamp} from "../../../../../shared/annotation/stamp/ExperimentalStamp";
import {NotApprovedStamp} from "../../../../../shared/annotation/stamp/NotApprovedStamp";
import {AsIsStamp} from "../../../../../shared/annotation/stamp/AsIsStamp";
import {ExpiredStamp} from "../../../../../shared/annotation/stamp/ExpiredStamp";
import {NotForPublicReleaseStamp} from "../../../../../shared/annotation/stamp/NotForPublicReleaseStamp";
import {ConfidentialStamp} from "../../../../../shared/annotation/stamp/ConfidentialStamp";
import {FinalStamp} from "../../../../../shared/annotation/stamp/FinalStamp";
import {SoldStamp} from "../../../../../shared/annotation/stamp/SoldStamp";
import {DepartmentalStamp} from "../../../../../shared/annotation/stamp/DepartmentalStamp";
import {ForCommentStamp} from "../../../../../shared/annotation/stamp/ForCommentStamp";
import {TopSecretStamp} from "../../../../../shared/annotation/stamp/TopSecretStamp";
import {DraftStamp} from "../../../../../shared/annotation/stamp/DraftStamp";
import {ForPublicReleaseStamp} from "../../../../../shared/annotation/stamp/ForPublicReleaseStamp";
import {Base64} from 'js-base64';
import {CustomStamp} from "../../../../../shared/annotation/stamp/CustomStamp";
import {PortalRubberStampAnnotation} from "../../../../../shared/models/PortalRubberStampAnnotation";
import {PortalToolboxAnnotationAnnotation} from "../../../../../shared/models/PortalToolboxAnnotationAnnotation";
import {ColorPicker} from "primereact/colorpicker";
import FunctionHelper from "../../../../../shared/helper/FunctionHelper";

export class AnnotationStampFunctionDefinition extends SidebarFunction<PortalToolboxAnnotationAnnotation,
	SidebarFunctionProperties<PortalToolboxAnnotationAnnotation>,
	SidebarFunctionState<PortalToolboxAnnotationAnnotation>> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<AnnotationStampFunctionFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editAnnotation, Functions.getEditAnnotationDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ANNOTATION_STAMP, signatureEditProfile, Intention.editingAnnotations, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["sidebar-function", "annotation-stamp-function"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<>
				{this.props.visible &&
					<div className={classList.join(" ")}>
						{this.renderHeader()}
						{this.renderContents()}
						{this.renderFooter()}
					</div>
				}
			</>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let errors: Map<AnnotationStampFunctionFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.add?.rubberstamp)) {
			return null;
		}

		let annotationParameter: PortalRubberStampAnnotation = parameter!.add!.rubberstamp![0];

		return (
			<>
				<div className="function-content">
					{this.renderError()}
					<fieldset className="p-col-12 p-grid">
						<legend>
							<Translate {...this.props} data="annotation.appearance.label" namespace="dialog"/>
						</legend>
						<div className="p-col-12 p-grid" hidden={
							annotationParameter.icon !== RubberStampIcon.Unnamed ||
							!this.state.fields?.contains(AnnotationStampFunctionFields.TEXT)
						}>
							<label className="p-col-12 p-sm-4" htmlFor="control-text">
								<Translate {...this.props} data="annotation.appearance.stamp.text" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<InputText id="control-text"
										   value={FunctionHelper.applyValue(
											   annotationParameter.text, PortalRubberStampAnnotation.getTextDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationStampFunctionFields.TEXT, e.currentTarget.value
										   )}/>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationStampFunctionFields.OPACITY)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-opacity">
								<Translate {...this.props} data="annotation.appearance.opacity" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<InputNumber inputId="control-opacity" showButtons={true} suffix=" %"
											 value={FunctionHelper.applyValue(
												 annotationParameter.opacity,
												 PortalRubberStampAnnotation.getOpacityDefault()
											 )}
											 step={1} min={PortalRubberStampAnnotation.getOpacityMin()}
											 max={PortalRubberStampAnnotation.getOpacityMax()} useGrouping={false}
											 onChange={(e) => this.onChange(
												 AnnotationStampFunctionFields.OPACITY, e.value
											 )}/>
							</div>
						</div>
						<div className="p-col-12 p-grid" hidden={
							annotationParameter.icon !== RubberStampIcon.Unnamed ||
							!this.state.fields?.contains(AnnotationStampFunctionFields.BORDER_COLOR)
						}>
							<label className="p-col-12 p-sm-4" htmlFor="control-border-color">
								<Translate {...this.props} data="annotation.appearance.color.border"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<div className={"p-inputgroup" + (
									errors.has(AnnotationStampFunctionFields.BORDER_COLOR) ? " p-invalid" : ""
								)}>
									<InputText
										className={
											errors.has(AnnotationStampFunctionFields.BORDER_COLOR) ? "p-invalid" : ""
										}
										tooltip={errors.has(AnnotationStampFunctionFields.BORDER_COLOR) ?
											Translate.value(
												errors.get(AnnotationStampFunctionFields.BORDER_COLOR)!,
												{ns: "message"}
											) : ""}
										value={FunctionHelper.applyValue(
											annotationParameter.borderColor,
											PortalRubberStampAnnotation.getBorderColorDefault()
										)}
										onInput={(e) => this.onChange(
											AnnotationStampFunctionFields.BORDER_COLOR, e.currentTarget.value
										)}/>
									<ColorPicker className="p-inputgroup-addon"
												 defaultColor={
													 PortalRubberStampAnnotation.getBorderColorDefault().replace(
														 "#", ""
													 )
												 }
												 inputId="control-border-color" format="hex"
												 value={FunctionHelper.applyValue(
													 annotationParameter.borderColor,
													 PortalRubberStampAnnotation.getBorderColorDefault()
												 ).replace("#", "")}
												 onChange={(e) => this.onChange(
													 AnnotationStampFunctionFields.BORDER_COLOR,
													 "#" + e.value
												 )} appendTo={document.getElementById("root")}/>
								</div>
								{this.renderFieldError(errors.has(AnnotationStampFunctionFields.BORDER_COLOR) ?
									Translate.value(
										errors.get(AnnotationStampFunctionFields.BORDER_COLOR)!, {ns: "message"}
									) : ""
								)}
							</div>
						</div>
						<div className="p-col-12 p-grid" hidden={
							annotationParameter.icon !== RubberStampIcon.Unnamed ||
							!this.state.fields?.contains(AnnotationStampFunctionFields.COLOR)
						}>
							<label className="p-col-12 p-sm-4" htmlFor="control-color">
								<Translate {...this.props} data="annotation.appearance.color.fill"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<div className={"p-inputgroup" + (
									errors.has(AnnotationStampFunctionFields.COLOR) ? " p-invalid" : ""
								)}>
									<InputText
										className={
											errors.has(AnnotationStampFunctionFields.COLOR) ? "p-invalid" : ""
										}
										tooltip={errors.has(AnnotationStampFunctionFields.COLOR) ? Translate.value(
											errors.get(AnnotationStampFunctionFields.COLOR)!, {ns: "message"}
										) : ""}
										value={FunctionHelper.applyValue(
											annotationParameter.color, PortalRubberStampAnnotation.getColorDefault()
										)}
										onInput={(e) => this.onChange(
											AnnotationStampFunctionFields.COLOR, e.currentTarget.value
										)}/>
									<ColorPicker className="p-inputgroup-addon"
												 defaultColor={PortalRubberStampAnnotation.getColorDefault().replace(
													 "#", ""
												 )}
												 inputId="control-color" format="hex"
												 value={FunctionHelper.applyValue(
													 annotationParameter.color,
													 PortalRubberStampAnnotation.getColorDefault()
												 ).replace("#", "")}
												 onChange={(e) => this.onChange(
													 AnnotationStampFunctionFields.COLOR, "#" + e.value
												 )} appendTo={document.getElementById("root")}/>
								</div>
								{this.renderFieldError(errors.has(AnnotationStampFunctionFields.COLOR) ?
									Translate.value(
										errors.get(AnnotationStampFunctionFields.COLOR)!, {ns: "message"}
									) : ""
								)}
							</div>
						</div>
						<div className="p-col-12 p-grid" hidden={
							annotationParameter.icon !== RubberStampIcon.Unnamed ||
							!this.state.fields?.contains(AnnotationStampFunctionFields.FONT_COLOR)
						}>
							<label className="p-col-12 p-sm-4" htmlFor="control-font-color">
								<Translate {...this.props} data="annotation.appearance.color.font"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<div className={"p-inputgroup" + (
									errors.has(AnnotationStampFunctionFields.FONT_COLOR) ? " p-invalid" : ""
								)}>
									<InputText
										className={
											errors.has(AnnotationStampFunctionFields.FONT_COLOR) ? "p-invalid" : ""
										}
										tooltip={errors.has(AnnotationStampFunctionFields.FONT_COLOR) ? Translate.value(
											errors.get(AnnotationStampFunctionFields.FONT_COLOR)!,
											{ns: "message"}
										) : ""}
										value={FunctionHelper.applyValue(
											annotationParameter.fontColor,
											PortalRubberStampAnnotation.getFontColorDefault()
										)}
										onInput={(e) => this.onChange(
											AnnotationStampFunctionFields.FONT_COLOR, e.currentTarget.value
										)}/>
									<ColorPicker className="p-inputgroup-addon"
												 defaultColor={
													 PortalRubberStampAnnotation.getFontColorDefault().replace(
														 "#", ""
													 )
												 }
												 inputId="control-font-color" format="hex"
												 value={FunctionHelper.applyValue(
													 annotationParameter.fontColor,
													 PortalRubberStampAnnotation.getFontColorDefault()
												 ).replace("#", "")}
												 onChange={(e) => this.onChange(
													 AnnotationStampFunctionFields.FONT_COLOR, "#" + e.value
												 )} appendTo={document.getElementById("root")}/>
								</div>
								{this.renderFieldError(errors.has(AnnotationStampFunctionFields.FONT_COLOR) ?
									Translate.value(
										errors.get(AnnotationStampFunctionFields.FONT_COLOR)!, {ns: "message"}
									) : ""
								)}
							</div>
						</div>
						<div className="p-col-12 p-grid" hidden={
							annotationParameter.icon !== RubberStampIcon.Unnamed ||
							!this.state.fields?.contains(AnnotationStampFunctionFields.FONT_SIZE)
						}>
							<label className="p-col-12 p-sm-4" htmlFor="control-font-size">
								<Translate {...this.props} data="annotation.appearance.fontsize"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<InputNumber inputId="control-font-size" step={1} showButtons={true} useGrouping={false}
											 value={FunctionHelper.applyValue(
												 annotationParameter.fontSize,
												 PortalRubberStampAnnotation.getFontSizeDefault()
											 )}
											 min={PortalRubberStampAnnotation.getFontSizeMin()}
											 max={PortalRubberStampAnnotation.getFontSizeMax()}
											 onChange={(e) => this.onChange(
												 AnnotationStampFunctionFields.FONT_SIZE, e.value
											 )}/>
							</div>
						</div>
					</fieldset>
					<fieldset className="p-col-12 p-grid">
						<legend>
							<Translate {...this.props} data="annotation.properties.label" namespace="dialog"/>
						</legend>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationStampFunctionFields.CREATOR)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-creator">
								<Translate {...this.props} data="annotation.properties.creator.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputText id="control-creator"
										   className={
											   errors.has(AnnotationStampFunctionFields.CREATOR) ?
												   "p-invalid" : ""
										   }
										   tooltip={errors.has(AnnotationStampFunctionFields.CREATOR) ?
											   Translate.value(
												   errors.get(AnnotationStampFunctionFields.CREATOR)!,
												   {ns: "message"}
											   ) : ""}
										   value={FunctionHelper.applyValue(
											   annotationParameter.creator,
											   PortalRubberStampAnnotation.getCreatorDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationStampFunctionFields.CREATOR, e.currentTarget.value
										   )}/>
								{this.renderFieldError(errors.has(AnnotationStampFunctionFields.CREATOR) ?
									Translate.value(
										errors.get(AnnotationStampFunctionFields.CREATOR)!, {ns: "message"}
									) : ""
								)}
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationStampFunctionFields.SUBJECT)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-subject">
								<Translate {...this.props} data="annotation.properties.subject.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputText id="control-subject"
										   value={FunctionHelper.applyValue(
											   annotationParameter.subject,
											   PortalRubberStampAnnotation.getSubjectDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationStampFunctionFields.SUBJECT, e.currentTarget.value
										   )}/>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationStampFunctionFields.CONTENTS)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-contents">
								<Translate {...this.props} data="annotation.properties.content.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputTextarea rows={3} id="control-contents"
											   value={FunctionHelper.applyValue(
												   annotationParameter.contents,
												   PortalRubberStampAnnotation.getContentsDefault()
											   )}
											   onInput={(e) => this.onChange(
												   AnnotationStampFunctionFields.CONTENTS, e.currentTarget.value
											   )}/>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(AnnotationStampFunctionFields.INTENTS)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-intents">
								<Translate {...this.props} data="annotation.properties.intents"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputText id="control-intents"
										   value={FunctionHelper.applyValue(
											   annotationParameter.intents,
											   PortalRubberStampAnnotation.getIntentsDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationStampFunctionFields.INTENTS, e.currentTarget.value
										   )}/>
							</div>
						</div>
					</fieldset>
				</div>
			</>
		);
	}

	protected onChange(field: AnnotationStampFunctionFields, value: any): void {
		let parameter: PortalToolboxAnnotationAnnotation = this.state.parameter!.data;
		let annotationParameter: PortalRubberStampAnnotation = parameter.add!.rubberstamp![0];
		let errors: Map<AnnotationStampFunctionFields, string> = this.state.fieldErrors;

		switch (field) {
			case AnnotationStampFunctionFields.CREATOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.creator = value;
				break;
			case AnnotationStampFunctionFields.SUBJECT:
				annotationParameter.subject = value;
				break;
			case AnnotationStampFunctionFields.CONTENTS:
				annotationParameter.contents = value;
				break;
			case AnnotationStampFunctionFields.INTENTS:
				annotationParameter.intents = value;
				break;
			case AnnotationStampFunctionFields.OPACITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalRubberStampAnnotation.getOpacityDefault();
				}

				annotationParameter.opacity = value;
				break;
			case AnnotationStampFunctionFields.COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.color = value;
				break;
			case AnnotationStampFunctionFields.BORDER_COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.borderColor = value;
				break;
			case AnnotationStampFunctionFields.FONT_COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.fontColor = value;
				break;
			case AnnotationStampFunctionFields.FONT_SIZE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalRubberStampAnnotation.getFontSizeDefault();
				}

				annotationParameter.fontSize = value;
				break;
			case AnnotationStampFunctionFields.TEXT:
				annotationParameter.text = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});

		this.props.context.getPage().setState({
			mouseFunctionAnnotationParameter: Immutable.from(this.state.parameter?.data.toJson()),
		});
	}

	protected renderHeader(): ReactNode {
		return null;
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add?.rubberstamp)) {
			return;
		}

		let annotationParameter: PortalRubberStampAnnotation = parameter!.add!.rubberstamp![0];

		if (annotationParameter.creator === "") {
			let user: UserCredentials = this.props.context.session.getUser()!;

			annotationParameter.creator = FunctionHelper.applyStringValue(
				user.userName, Translate.value("annotation.properties.creator.default", {ns: "dialog"})
			);
		}

		if (annotationParameter.subject === "") {
			annotationParameter.subject =
				Translate.value("annotation.properties.subject.default.stamp", {ns: "dialog"});
		}

		if (annotationParameter.contents === "") {
			annotationParameter.contents =
				Translate.value("annotation.properties.content.default", {ns: "dialog"});
		}

		if (annotationParameter.text === "") {
			annotationParameter.text =
				Translate.value("annotation.appearance.stamp.default", {ns: "dialog"});
		}

		this.setState({
			parameter: this.state.parameter
		});
	};

	private getStampAppearance(): string {
		let parameter: PortalToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let annotationParameter: PortalRubberStampAnnotation = parameter!.add!.rubberstamp![0];
		let opacity: number = FunctionHelper.applyValue(
			annotationParameter.opacity, PortalRubberStampAnnotation.getOpacityDefault()
		);

		let width: number = FunctionHelper.applyValue(
			annotationParameter.position?.rectangle?.width, Rectangle.getWidthDefault()
		);
		let height: number = FunctionHelper.applyValue(
			annotationParameter.position?.rectangle?.height, Rectangle.getHeightDefault()
		);
		let color: string = FunctionHelper.applyValue(
			annotationParameter.color, PortalRubberStampAnnotation.getColorDefault()
		);
		let borderColor: string = FunctionHelper.applyValue(
			annotationParameter.borderColor, PortalRubberStampAnnotation.getBorderColorDefault()
		);
		let fontColor: string = FunctionHelper.applyValue(
			annotationParameter.fontColor, PortalRubberStampAnnotation.getFontColorDefault()
		);
		let fontSize: number = FunctionHelper.applyValue(
			annotationParameter.fontSize, PortalRubberStampAnnotation.getFontSizeDefault()
		);
		let text: string = FunctionHelper.applyValue(
			annotationParameter.text, PortalRubberStampAnnotation.getTextDefault()
		);

		let appearance: ReactElement = <></>;
		switch (annotationParameter.icon) {
			case RubberStampIcon.Approved:
				appearance =
					<ApprovedStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.Experimental:
				appearance =
					<ExperimentalStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.NotApproved:
				appearance =
					<NotApprovedStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.AsIs:
				appearance =
					<AsIsStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.Expired:
				appearance =
					<ExpiredStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.NotForPublicRelease:
				appearance =
					<NotForPublicReleaseStamp {...this.props} opacity={opacity} width={width} height={height}
											  color={color}/>;
				break;
			case RubberStampIcon.Confidential:
				appearance =
					<ConfidentialStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.Final:
				appearance =
					<FinalStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.Sold:
				appearance =
					<SoldStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.Departmental:
				appearance =
					<DepartmentalStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.ForComment:
				appearance =
					<ForCommentStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.TopSecret:
				appearance =
					<TopSecretStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.Draft:
				appearance =
					<DraftStamp {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case RubberStampIcon.ForPublicRelease:
				appearance =
					<ForPublicReleaseStamp {...this.props} opacity={opacity} width={width} height={height}
										   color={color}/>;
				break;
			case RubberStampIcon.Unnamed:
				appearance =
					<CustomStamp {...this.props} opacity={opacity} width={width} height={height} color={color}
								 borderColor={borderColor} fontColor={fontColor} fontSize={fontSize}>
						{text}
					</CustomStamp>;
				break;
		}

		return ReactDOMServer.renderToStaticMarkup(appearance);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		let parameter: PortalToolboxAnnotationAnnotation = this.state.parameter!.data;
		let annotationParameter: PortalRubberStampAnnotation = parameter!.add!.rubberstamp![0];
		annotationParameter.normalAppearance!.svg!.data.value = Base64.toBase64(this.getStampAppearance());

		let operationData: Array<BaseToolbox> = [
			BaseToolbox.fromJson({
				annotation: this.state.parameter!.data.toJson()
			})
		];

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.annotation.stamp", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ANNOTATION_STAMP;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxAnnotationAnnotation> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const AnnotationStampFunction = React.memo(AnnotationStampFunctionDefinition,
	(
		prevProps: Readonly<SidebarFunctionProperties<PortalToolboxAnnotationAnnotation>>,
		nextProps: Readonly<SidebarFunctionProperties<PortalToolboxAnnotationAnnotation>>
	) => {
		if (!nextProps.visible && !prevProps.visible && prevProps.visible !== nextProps.visible) {
			return true
		}

		return false;
	}
);

export default AnnotationStampFunction;